import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Hospital {
  id: string;
  name: string;
  address: string;
  phone: string;
  comment: string;
  type: 'hospital' | 'clinic';
  tags: string[];
}

interface FilterTag {
  id: string;
  name: string;
}

const defaultTags: FilterTag[] = [
  { id: 'cpx', name: '心肺運動負荷試験（CPX）対応' },
  { id: 'rehab', name: '外来リハビリテーション紹介' },
  { id: 'other', name: 'その他' }
];

const ReferralHospitals = () => {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [filterTags, setFilterTags] = useState<FilterTag[]>(defaultTags);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showHospitalForm, setShowHospitalForm] = useState(false);
  const [editingHospital, setEditingHospital] = useState<Hospital | null>(null);
  const [newTagName, setNewTagName] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    comment: '',
    type: 'hospital' as 'hospital' | 'clinic',
    tags: [] as string[]
  });

  useEffect(() => {
    loadHospitals();
    loadFilterTags();
  }, []);

  const loadHospitals = () => {
    const stored = localStorage.getItem('referralHospitals');
    if (stored) {
      setHospitals(JSON.parse(stored));
    } else {
      const defaultHospitals: Hospital[] = [
        {
          id: '1',
          name: '港区立心臓リハビリテーションセンター',
          address: '東京都港区芝公園1-2-3',
          phone: '03-1234-5678',
          comment: '外来心臓リハビリテーション、心肺運動負荷試験（CPX）、運動処方箋対応が可能です。',
          type: 'hospital',
          tags: ['cpx', 'rehab']
        },
        {
          id: '2',
          name: '東京循環器クリニック',
          address: '東京都港区赤坂2-3-4',
          phone: '03-2345-6789',
          comment: '外来心臓リハビリテーション、栄養指導、生活指導を行っております。',
          type: 'clinic',
          tags: ['rehab']
        },
        {
          id: '3',
          name: '虎ノ門リハビリテーション病院',
          address: '東京都港区虎ノ門3-4-5',
          phone: '03-3456-7890',
          comment: '外来心臓リハビリテーション、心肺運動負荷試験（CPX）、多職種連携、運動処方箋対応が可能です。',
          type: 'hospital',
          tags: ['cpx', 'rehab']
        },
        {
          id: '4',
          name: '港南メディカルセンター',
          address: '東京都港区港南4-5-6',
          phone: '03-4567-8901',
          comment: '外来心臓リハビリテーション、生活指導を行っております。',
          type: 'clinic',
          tags: ['rehab']
        }
      ];
      localStorage.setItem('referralHospitals', JSON.stringify(defaultHospitals));
      setHospitals(defaultHospitals);
    }
  };

  const loadFilterTags = () => {
    const stored = localStorage.getItem('referralFilterTags');
    if (stored) {
      setFilterTags(JSON.parse(stored));
    } else {
      localStorage.setItem('referralFilterTags', JSON.stringify(defaultTags));
      setFilterTags(defaultTags);
    }
  };

  const handleAdminLogin = () => {
    if (password === 'cr.toranomon') {
      setIsAdmin(true);
      setPassword('');
      setShowAdminPanel(false);
    } else {
      alert('パスワードが正しくありません');
    }
  };

  const handleAddTag = () => {
    if (!newTagName.trim()) {
      alert('タグ名を入力してください');
      return;
    }
    const newTag: FilterTag = {
      id: `tag-${Date.now()}`,
      name: newTagName.trim()
    };
    const updated = [...filterTags, newTag];
    setFilterTags(updated);
    localStorage.setItem('referralFilterTags', JSON.stringify(updated));
    setNewTagName('');
    alert('タグを追加しました');
  };

  const handleDeleteTag = (tagId: string) => {
    if (!window.confirm('このタグを削除しますか？')) return;
    const updated = filterTags.filter(tag => tag.id !== tagId);
    setFilterTags(updated);
    localStorage.setItem('referralFilterTags', JSON.stringify(updated));
    if (activeFilter === tagId) setActiveFilter('all');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingHospital) {
      const updated = hospitals.map(h => 
        h.id === editingHospital.id 
          ? { ...h, ...formData }
          : h
      );
      localStorage.setItem('referralHospitals', JSON.stringify(updated));
      setHospitals(updated);
      setEditingHospital(null);
      alert('医療機関情報を更新しました');
    } else {
      const newHospital: Hospital = {
        id: Date.now().toString(),
        ...formData
      };
      const updated = [...hospitals, newHospital];
      localStorage.setItem('referralHospitals', JSON.stringify(updated));
      setHospitals(updated);
      alert('医療機関を追加しました');
    }
    
    setFormData({ name: '', address: '', phone: '', comment: '', type: 'hospital', tags: [] });
    setShowHospitalForm(false);
  };

  const handleEdit = (hospital: Hospital) => {
    setEditingHospital(hospital);
    setFormData({
      name: hospital.name,
      address: hospital.address,
      phone: hospital.phone,
      comment: hospital.comment,
      type: hospital.type,
      tags: hospital.tags
    });
    setShowHospitalForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingHospital(null);
    setFormData({ name: '', address: '', phone: '', comment: '', type: 'hospital', tags: [] });
    setShowHospitalForm(false);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('この医療機関を削除しますか？')) {
      const updated = hospitals.filter(h => h.id !== id);
      localStorage.setItem('referralHospitals', JSON.stringify(updated));
      setHospitals(updated);
    }
  };

  const handleTagToggle = (tagId: string) => {
    const newTags = formData.tags.includes(tagId)
      ? formData.tags.filter(t => t !== tagId)
      : [...formData.tags, tagId];
    setFormData({ ...formData, tags: newTags });
  };

  const filteredHospitals = activeFilter === 'all'
    ? hospitals
    : hospitals.filter(h => h.tags.includes(activeFilter));

  const getTagName = (tagId: string) => {
    const tag = filterTags.find(t => t.id === tagId);
    return tag ? tag.name : tagId;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              紹介実績のある病院一覧
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              当院から退院後の心臓リハビリテーションをご紹介している医療機関の一覧です
            </p>
          </div>
        </div>
      </section>

      {/* Admin Login */}
      {!isAdmin && (
        <div className="py-4 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-right">
            <button
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              管理者ログイン
            </button>
          </div>
        </div>
      )}

      {showAdminPanel && !isAdmin && (
        <div className="py-8 bg-gray-50">
          <div className="max-w-md mx-auto px-4 sm:px-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">管理者ログイン</h3>
              <div className="flex gap-3">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワードを入力"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                />
                <button
                  onClick={handleAdminLogin}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm cursor-pointer"
                >
                  ログイン
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel */}
      {isAdmin && (
        <section className="py-8 bg-teal-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-bold text-gray-900">管理者モード</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowTagManager(!showTagManager);
                    setShowHospitalForm(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-price-tag-3-line"></i>
                  タグ管理
                </button>
                <button
                  onClick={() => {
                    setShowHospitalForm(!showHospitalForm);
                    setShowTagManager(false);
                    if (!showHospitalForm) {
                      setEditingHospital(null);
                      setFormData({ name: '', address: '', phone: '', comment: '', type: 'hospital', tags: [] });
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  {showHospitalForm ? 'キャンセル' : '医療機関を追加'}
                </button>
                <button
                  onClick={() => setIsAdmin(false)}
                  className="text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  ログアウト
                </button>
              </div>
            </div>

            {/* Tag Manager */}
            {showTagManager && (
              <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">絞り込みタグ管理</h3>
                
                <div className="mb-6 flex gap-2">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="新しいタグ名を入力"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    追加
                  </button>
                </div>

                <div className="space-y-2">
                  {filterTags.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm font-medium text-gray-900">{tag.name}</span>
                      <button
                        onClick={() => handleDeleteTag(tag.id)}
                        className="text-red-500 hover:text-red-700 cursor-pointer p-1"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hospital Form */}
            {showHospitalForm && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {editingHospital ? '医療機関情報を編集' : '新しい医療機関を追加'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        医療機関名
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="医療機関名を入力"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        種別
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'hospital' | 'clinic' })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      >
                        <option value="hospital">病院</option>
                        <option value="clinic">診療所</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      住所
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="住所を入力"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      代表番号
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="03-1234-5678"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      コメント（2-3行）
                    </label>
                    <textarea
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="対応可能な内容などを入力"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      対応内容タグ
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {filterTags.map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleTagToggle(tag.id)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                            formData.tags.includes(tag.id)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                    >
                      {editingHospital ? '更新する' : '追加する'}
                    </button>
                    {editingHospital && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        キャンセル
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-gray-700 font-medium">絞り込み：</span>
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeFilter === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              すべて
            </button>
            {filterTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => setActiveFilter(tag.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeFilter === tag.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Hospitals List */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {filteredHospitals.map((hospital) => (
              <div
                key={hospital.id}
                className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className={`${hospital.type === 'hospital' ? 'w-16 h-16' : 'w-12 h-12'} bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${hospital.type === 'hospital' ? 'ri-hospital-line text-3xl' : 'ri-building-line text-2xl'} text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {hospital.name}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {hospital.tags.map(tagId => (
                        <span key={tagId} className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                          {getTagName(tagId)}
                        </span>
                      ))}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(hospital)}
                        className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                      >
                        <i className="ri-edit-line text-xl"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(hospital.id)}
                        className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                      >
                        <i className="ri-delete-bin-line text-xl"></i>
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-teal-500 text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-gray-600">{hospital.address}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-teal-500 text-lg flex-shrink-0"></i>
                    <a
                      href={`tel:${hospital.phone.replace(/-/g, '')}`}
                      className="text-gray-600 hover:text-teal-500 transition-colors cursor-pointer"
                    >
                      {hospital.phone}
                    </a>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-600 text-sm leading-relaxed">{hospital.comment}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredHospitals.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-4xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 text-lg">該当する医療機関が見つかりませんでした</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-2xl border border-teal-100">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-question-line text-3xl text-white"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              ご紹介に関するお問い合わせ
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              上記医療機関への紹介状の作成や相談などは、心臓リハビリテーション外来で行っております。ご質問やご相談がありましたら地域連携室を通して、外来心臓リハビリテーションのご予約をお取りください。
            </p>
            <a
              href="tel:03-3588-1111"
              className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 text-white text-base font-medium rounded-lg hover:bg-teal-600 transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              03-3588-1111（代表）
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReferralHospitals;
